import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Tooltip,
  Typography,
} from '@mui/material';

import ProductRow from './ProductRow';
import LeadTimeDropdown from './LeadTimeDropdown';
import { Product } from '@domain/product/models/Product';
import { useProductUpdate } from '../../hooks/useProductUpdate';
import { Info } from '@mui/icons-material';
interface ProductsTableProps {
  showCheckboxColumn: boolean;
  products: Product[];
  selectedProducts: Product[];
  onSelectionChange?: (selectedProducts: Product[]) => void;
}

const ProductsTable: React.FC<ProductsTableProps> = ({
  showCheckboxColumn,
  selectedProducts,
  onSelectionChange,
  products
}) => {

  const [leadTimeDropdown, setLeadTimeDropdown] = useState<{
    product?: Product;
    anchorEl: HTMLElement | null;
  }>({ product: undefined, anchorEl: null });

  // Removed unnecessary useProducts call that was causing query conflicts

  const { updateProduct } = useProductUpdate();

  // Update parent component when selection changes
  useEffect(() => {
    onSelectionChange?.(selectedProducts);
  }, [selectedProducts, onSelectionChange]);

  // Clear selection when checkbox column is hidden
  useEffect(() => {
    if (!showCheckboxColumn) {
      onSelectionChange?.([]);
    }
  }, [showCheckboxColumn]);

  const handleProductSelect = useCallback(
    (product: Product, selected: boolean) => {
      if (!onSelectionChange) return;

      if (selected) {
        // Add if not already selected
        if (!selectedProducts.some((p) => p.id === product.id)) {
          onSelectionChange([...selectedProducts, product]);
        }
      } else {
        // Remove if present
        onSelectionChange(selectedProducts.filter((p) => p.id !== product.id));
      }
    },
    [onSelectionChange, selectedProducts]
  );


  const handleSelectAll = useCallback((selected: boolean) => {
    if (selected) {
      onSelectionChange?.([...products]);
    } else {
      onSelectionChange?.([]);
    }
  }, [products]);

  const isAllSelected = useMemo(() =>
    products.length > 0 && selectedProducts.length === products.length,
    [products.length, selectedProducts.length]
  );

  const isIndeterminate = useMemo(() =>
    selectedProducts.length > 0 && selectedProducts.length < products.length,
    [selectedProducts.length, products.length]
  );

  const handleLeadTimeClick = useCallback((product: Product, event: React.MouseEvent<HTMLDivElement>) => {
    setLeadTimeDropdown({
      product: product,
      anchorEl: event.currentTarget
    });
  }, []);

  const handleLeadTimeClose = useCallback(() => {
    setLeadTimeDropdown({ product: undefined, anchorEl: null });
  }, []);

  const handleLeadTimeConfirm = useCallback(
    (values: {
      productionDuration?: number;
      processingDuration?: number;
      defaultShipmentMethodId?: string;
    }) => {
      if (leadTimeDropdown.product) {
        updateProduct({
          productId: leadTimeDropdown.product.id,
          updateData: {
            productionDuration: values.productionDuration,
            processingDuration: values.processingDuration,
            ...(values.defaultShipmentMethodId != null
              ? { defaultShipmentMethodId: values.defaultShipmentMethodId }
              : {}),
          },
        });
      }

      handleLeadTimeClose();
    },
    [leadTimeDropdown.product, updateProduct, handleLeadTimeClose]
  );


  return (
    <TableContainer>
      <Table
        stickyHeader
      >
        <TableHead>
          <TableRow>

            <TableCell padding="checkbox" sx={{ paddingLeft: 3 }}>
              <Checkbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            </TableCell>

            <TableCell sx={{ px: 3 }}>Title</TableCell>
            <TableCell sx={{ width: '150px' }}>Lead Time</TableCell>
            <TableCell sx={{ width: '180px' }}>
              <Tooltip title="For how many weeks do you want to order this product?">
                <Typography display="flex" alignItems="center" gap={1} variant="body2">Order Amount < Info fontSize="small" color="info" /> </Typography>
              </Tooltip>
            </TableCell>
            <TableCell sx={{ width: '200px' }}>
              <Tooltip title="For example, adding a 2-week buffer will prompt a reorder alert, ensuring that when new stock arrives you still have at least 2 weeks of inventory remaining.">
                <Typography display="flex" alignItems="center" gap={1} variant="body2">
                  Weeks of buffer< Info fontSize="small" color="info" /> </Typography>
              </Tooltip>
            </TableCell>
            <TableCell sx={{ width: '180px' }}>Mode</TableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={{
          '& tr:last-child': {
            '& td': { borderBottom: 0 }
          }
        }}>
          {products && products.length > 0 ? (
            products.map((product) => {
              const isSelected = selectedProducts.some(p => p.id === product.id);
              return (
                <ProductRow
                  key={product.id}
                  product={product}
                  onSelect={handleProductSelect}
                  isSelected={isSelected}
                  onLeadTimeClick={handleLeadTimeClick}
                />
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={showCheckboxColumn ? 8 : 7} sx={{ textAlign: 'center' }}>
                No products found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {leadTimeDropdown.product && (
        <LeadTimeDropdown
          product={leadTimeDropdown.product}
          anchorEl={leadTimeDropdown.anchorEl}
          open={Boolean(leadTimeDropdown.anchorEl)}
          onClose={handleLeadTimeClose}
          onConfirm={handleLeadTimeConfirm}
        />
      )}
    </TableContainer>

  );
};

export default ProductsTable; 